/* eslint-env browser */
import _ from 'lodash';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import * as Sentry from '@sentry/browser';
import Plausible from 'plausible-tracker';

import { derror } from 'frontend/util';
import SimcVersionSingleton from 'frontend/simcVersion';

import actionTypes from './actionTypes';

import reducer from './reducers/index';

const { trackEvent } = Plausible({ domain: 'raidbots.com' });

const EVENTS_TO_TRACK = {
  [actionTypes.SUBMIT_SIM_REQUEST]: 'sim_submit',
};

const crashReporter = () => next => action => {
  try {
    return next(action);
  } catch (err) {
    derror('Caught an exception in middleware', err);
    Sentry.captureException(err);
    throw err;
  }
};

const wowVersion = () => next => action => {
  next(action);

  if (action.type === actionTypes.SELECT_SIMC_VERSION) {
    SimcVersionSingleton.set(action.payload);
  }
};

let persistTimeout = null;

const saveToLocal = store => next => action => { // eslint-disable-line
  next(action);

  const ACTIONS_IGNORE = [
    actionTypes.FETCH_STATUS_REQUEST,
    actionTypes.FETCH_STATUS_SUCCESS,
    actionTypes.FETCH_STATUS_FAILURE,
  ];

  if (_.includes(ACTIONS_IGNORE, action.type)) {
    return;
  }

  if (persistTimeout) {
    clearTimeout(persistTimeout);
  }
  persistTimeout = setTimeout(() => {
    const state = _.cloneDeep(store.getState());

    /////////////////////////////////////////////////////
    // fields to null out in local storage
    /////////////////////////////////////////////////////

    delete state.simbot.realms;
    delete state.simbot.form.text;
    delete state.simbot.form.error;
    delete state.simbot.form.loading;
    delete state.simbot.form.data;
    delete state.simbot.form.baseActorName;
    delete state.simbot.form.email;
    delete state.simbot.items;
    delete state.simbot.gearsets;
    delete state.simbot.talentSets;
    delete state.simbot.simcItems;
    delete state.simbot.jobs;
    delete state.simbot.optimize.gear;
    delete state.simbot.optimize.selected;
    delete state.simbot.optimize.combinations;
    delete state.simbot.optimize.loading;
    delete state.simbot.optimize.lockedRing;
    delete state.simbot.optimize.lockedTrinket;
    delete state.simbot.optimize.preloadData;

    delete state.site.talentTreeOverlay;

    // version data from HTML
    delete state.version.initialVersion;
    delete state.version.initialVersionUpdated;

    delete state.user;
    delete state.flash;

    // try to save to localStorage, fail silently if something goes wrong
    // (private mode, out of space, etc)
    try {
      if (window && window.localStorage && window.localStorage.setItem) {
        window.localStorage.setItem('store', JSON.stringify(state));
      }
    } catch (ex) {
      // do nothing
    }
    persistTimeout = null;
  }, 100);
};

const track = () => next => action => {
  next(action);

  const ACTIONS_IGNORE = [
    actionTypes.INPUT_FORM,
    actionTypes.INPUT_REPORT_NAME,
    actionTypes.INPUT_ITEM,
    actionTypes.INPUT_ITEM_LEVEL,
    actionTypes.INPUT_TALENT_SET_NAME,
    actionTypes.INPUT_GEARSET_NAME,

    actionTypes.REMOVE_JOB,

    actionTypes.SUBMIT_SIM_SUCCESS,
    actionTypes.SUBMIT_SIM_FAILURE,

    actionTypes.FETCH_CHAR_REQUEST,
    actionTypes.FETCH_CHAR_SUCCESS,
    actionTypes.FETCH_CHAR_FAILURE,

    actionTypes.FETCH_REALMS_REQUEST,
    actionTypes.FETCH_REALMS_SUCCESS,
    actionTypes.FETCH_REALMS_FAILURE,

    actionTypes.FETCH_SIMS_REQUEST,
    actionTypes.FETCH_SIMS_FAILURE,
    actionTypes.FETCH_SIMS_SUCCESS,

    actionTypes.FETCH_SIMC_ITEMS_REQUEST,
    actionTypes.FETCH_SIMC_ITEMS_SUCCESS,
    actionTypes.FETCH_SIMC_ITEMS_FAILURE,

    actionTypes.FETCH_STATUS_REQUEST,
    actionTypes.FETCH_STATUS_SUCCESS,
    actionTypes.FETCH_STATUS_FAILURE,
  ];

  if (EVENTS_TO_TRACK[action.type]) {
    trackEvent(EVENTS_TO_TRACK[action.type]);
  }

  if (!_.includes(ACTIONS_IGNORE, action.type)) {
    // TODO: Evaluate if sentry should track everything or if it should respect the same ignored actions
    Sentry.addBreadcrumb({
      category: 'redux',
      message: action.type,
      level: 'info',
    });
  }
};

export default (initialState) => {
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // eslint-disable-line
  const store = createStore(
    reducer,
    initialState,
    composeEnhancers(
      applyMiddleware(
        crashReporter,
        thunk,
        track,
        wowVersion,
        saveToLocal,
      ),
    ),
  );

  return store;
};
